import { useLayoutEffect, useRef, useState, type KeyboardEvent } from "react";

/**
 * s226 (Reade: "ok agreed", board v345) — A CUT TEXT IS ITS OWN WAY TO READ IT (rule 133, D-223.13: a control in
 * the same cell that shows the full value). A SHARED piece (G7).
 *
 * Rule 133(a) keeps a row one height, so a long value is clamped in its cell. This makes the clamped text itself
 * the control — no icon (an icon takes room from a narrow line and breaks short names mid-word, measured on the
 * board's mockup):
 *
 *   • `FullTextCell` — the text, clamped by the caller's class. ONLY when the build measures it clipped
 *     (scrollHeight > clientHeight) does it become a button: dotted underline (#6B655A, 3px offset), pointer,
 *     keyboard-reachable, aria-expanded. A text that fits is plain words — no underline, not clickable.
 *   • `FullTextPanel` — under the row: "<label> <text>" on #ECE7DC, 14px, radius 8, read-only. Click again closes.
 *
 * 🔴 ONE SHARED PIECE (Reade, G7): nothing here is about names. It is for any cut value in a table — daily's name
 * cell first (s226), then closed-row values, the grey second line, and the home's Contacts / Where things are
 * lists. Each caller brings its own clamp (`textClassName`), its own words and its own open state.
 *
 * The words ("Full name:", "Show the full name") are the caller's, from the board. The open state is the caller's
 * too (the row table's `usePanels`, so rule 66 — one thing open at a time — holds).
 *
 * 🔴 NOT an editing control: `LocationBox` writes; this only reads.
 */

/**
 * Clipped = the content is taller than the clamp lets it show, or (s227, a one-line ellipsis like the board's read
 * cell) wider than its box. Pure, for the tests.
 */
export function isClipped(
  el: (Pick<HTMLElement, "scrollHeight" | "clientHeight"> & Partial<Pick<HTMLElement, "scrollWidth" | "clientWidth">>) | null,
): boolean {
  if (!el) return false;
  return el.scrollHeight - el.clientHeight > 1 || (el.scrollWidth ?? 0) - (el.clientWidth ?? 0) > 1;
}

export function FullTextCell({
  text,
  open,
  onToggle,
  textClassName,
  title,
  testId,
}: {
  text: string;
  open: boolean;
  onToggle: () => void;
  /** The clamp and type of the text itself (the caller's cell style, e.g. a 3-line clamp). */
  textClassName: string;
  /** The hover title while the text is cut (the board's words). */
  title: string;
  testId: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const [clipped, setClipped] = useState(false);

  // Measured after layout, and again whenever the cell resizes (a narrower window clips more).
  useLayoutEffect(() => {
    const el = ref.current;
    const measure = () => setClipped(isClipped(ref.current));
    measure();
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [text]);

  const onKey = (e: KeyboardEvent<HTMLSpanElement>) => {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(); }
  };
  const cut = clipped || open;
  return (
    <span
      ref={ref}
      className={`${textClassName} rounded-[6px]` + (cut
        ? " cursor-pointer underline decoration-dotted decoration-warm-muted-line underline-offset-[3px]" + (open ? " bg-warm-stone-light" : "")
        : "")}
      {...(cut
        ? { role: "button", tabIndex: 0, "aria-expanded": open, title, onClick: onToggle, onKeyDown: onKey, "data-reveals": `${testId}-full` }
        : {})}
      data-testid={testId}
    >
      {text}
    </span>
  );
}

export function FullTextPanel({ label, text, testId }: { label: string; text: string; testId: string }) {
  return (
    <div className="mx-0.5 mb-2 rounded-[8px] bg-warm-stone-light px-3 py-2.5 text-[14px] text-warm-charcoal [overflow-wrap:anywhere]"
      data-testid={`${testId}-full`}>
      <span className="font-medium">{label}</span> {text}
    </div>
  );
}
